import React, { useState } from 'react';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { Box, Icon, Text, useBreakpointValue } from '@chakra-ui/react';
import { useHouseContext } from '@/context/HouseContext.tsx';
import PropertySelectorMobile from '@/components/properties/selector/PropertySelectorMobile.tsx';
import PropertySelectorDesktop from '@/components/properties/selector/PropertySelectorDesktop.tsx';
import { LuArchive, LuMapPin } from 'react-icons/lu';
import { splitAddress } from '@/utils/properties/addressUtils.ts';
import { isOwnedArchiveCopy } from '@/utils/properties/propertyGroupingUtils';
import { useAuth } from '@/context/AuthContext';
import PropertyCopyTag from '@/components/properties/PropertyCopyTag';
import { useLockedPropertyIds } from '@/hooks/properties/usePropertyPaywallState';
import { useUnreadNotificationPropertyIds } from '@/hooks/notifications/useUnreadNotificationPropertyIds';
import PropertySelectorChevrons from './PropertySelectorChevrons';

/** The trigger's observers never remount, so staleness alone would never refresh its dot. */
const UNREAD_MARKER_REFRESH_MS = 5 * 60_000;

export const PropertySelector: React.FC = () => {
	const { houses, selectedHouse } = useHouseContext();
	const { user } = useAuth();
	const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false });
	const [mobileOpen, setMobileOpen] = useState(false);
	const lockedPropertyIds = useLockedPropertyIds();
	// Owned here rather than in the menus because the trigger is always on screen and the mobile sheet mounts only when open.
	const unreadNotificationPropertyIds = useUnreadNotificationPropertyIds(houses, {
		excludedHouseIds: lockedPropertyIds,
		refetchInterval: UNREAD_MARKER_REFRESH_MS,
	});

	// Don't render if no house is selected
	if (!selectedHouse) {
		return null;
	}

	const formattedAddress = splitAddress(selectedHouse.address).street;
	// The selected property's own unread items are the bell's job, so only another property lights the trigger.
	const hasUnreadElsewhere =
		unreadNotificationPropertyIds.size > (unreadNotificationPropertyIds.has(selectedHouse.id) ? 1 : 0);

	if (isMobile) {
		return (
			<>
				<Box w="full" minW={0}>
					<OutlinedButton
						onClick={() => setMobileOpen(true)}
						size="md"
						fontWeight="medium"
						w="100%"
						h="40px"
						minW={0}
						px={2}
						border="none"
						borderRadius="full"
						bg="transparent"
						_hover={{ bg: 'surface.subtle' }}
					>
						<Icon
							as={isOwnedArchiveCopy(selectedHouse, user?.id) ? LuArchive : LuMapPin}
							boxSize={5}
							flexShrink={0}
						/>
						<Text fontSize="sm" truncate minW={0} maxW="100%">
							{formattedAddress}
						</Text>
						<PropertyCopyTag isCopy={selectedHouse.isCopy} />
						<PropertySelectorChevrons hasUnreadElsewhere={hasUnreadElsewhere} />
					</OutlinedButton>
				</Box>
				{mobileOpen && (
					<PropertySelectorMobile
						open={mobileOpen}
						onOpenChange={setMobileOpen}
						unreadNotificationPropertyIds={unreadNotificationPropertyIds}
					/>
				)}
			</>
		);
	}

	return (
		<PropertySelectorDesktop
			unreadNotificationPropertyIds={unreadNotificationPropertyIds}
			hasUnreadElsewhere={hasUnreadElsewhere}
		/>
	);
};

export default PropertySelector;
